/**
 * Emergency Booking Notification Badge
 * Red alert badge that appears in navigation when emergency bookings are pending
 * Shows dropdown panel with booking details when clicked
 */

import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, Clock, User, Calendar, ChevronRight, RefreshCw } from 'lucide-react';
import { emergencyBookingService, EmergencyBookingRequest } from '../../services/emergencyBookingService';
import { clientDataService } from '../../services/clientDataService';

export const EmergencyBookingNotificationBadge: React.FC = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPulse, setShowPulse] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pendingBookings, setPendingBookings] = useState<EmergencyBookingRequest[]>([]);
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Update count every 5 seconds
    const updateCount = () => {
      const count = emergencyBookingService.getUnreadCount();
      const bookings = emergencyBookingService.getPendingBookings();
      setUnreadCount(count);
      setPendingBookings(bookings);

      // Trigger pulse animation on new notifications
      if (count > 0) {
        setShowPulse(true);
        setTimeout(() => setShowPulse(false), 1000);
      }
    };

    // Force refresh from Firebase server on mount to bypass cache
    emergencyBookingService.forceRefresh().then(() => {
      updateCount();
    });

    updateCount();
    const interval = setInterval(updateCount, 5000);

    // Subscribe to booking updates
    const unsubscribe = emergencyBookingService.subscribe(() => {
      updateCount();
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleApprove = async (booking: EmergencyBookingRequest) => {
    console.log('Approving booking:', booking.id);
    try {
      await emergencyBookingService.approveBookingSimple(booking.id);
      console.log('Booking approved successfully');

      // Mark notification as read
      const notifications = emergencyBookingService.getStaffNotifications();
      const notification = notifications.find(n => n.bookingId === booking.id);
      if (notification) {
        emergencyBookingService.markNotificationAsRead(notification.id);
      }

      // Mark as seen locally
      emergencyBookingService.markBookingAsSeen(booking.id);

      // Force refresh from service
      setTimeout(() => {
        const count = emergencyBookingService.getUnreadCount();
        const bookings = emergencyBookingService.getPendingBookings();
        console.log('After approve - count:', count, 'bookings:', bookings.length);
        setUnreadCount(count);
        setPendingBookings(bookings);
        if (bookings.length === 0) setShowDropdown(false);
      }, 500);

      // Show success feedback
      alert('✅ Booking approved!');
    } catch (error) {
      console.error('Error approving booking:', error);
      alert('❌ Failed to approve booking. Check console for details.');
    }
  };

  const getClientName = (clientId: string) => {
    try {
      const client = clientDataService.getClientById(clientId);
      return client ? `${client.firstName} ${client.lastName}` : 'Unknown Client';
    } catch {
      return 'Unknown Client';
    }
  };

  const formatTime = (dateInput: string | Date) => {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateInput: string | Date) => {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Always show badge for debugging - shows count or "0" if no pending
  // if (unreadCount === 0) {
  //   return null;
  // }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative group"
        title={`${unreadCount} pending emergency booking${unreadCount > 1 ? 's' : ''}`}
      >
        {/* Red Alert Badge */}
        <div className={`
          relative flex items-center gap-2 px-3 py-2 rounded-lg
          ${unreadCount > 0
            ? 'bg-gradient-to-r from-red-600 to-orange-600'
            : 'bg-gray-400'
          }
          text-white font-semibold shadow-lg
          hover:opacity-90
          transition-all duration-300
          ${showPulse ? 'animate-pulse scale-110' : ''}
        `}>
          <Bell className={`h-5 w-5 ${showPulse ? 'animate-bounce' : ''}`} />
          <span className="text-sm">{unreadCount > 0 ? '🚨' : '📭'} {unreadCount}</span>

          {/* Pulsing Ring Animation */}
          {!showDropdown && unreadCount > 0 && (
            <span className="absolute inset-0 rounded-lg border-2 border-red-400 animate-ping opacity-75" />
          )}
        </div>
      </button>

      {/* Dropdown Panel - Always dark for consistency */}
      {showDropdown && (
        <div className="
          absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 max-h-96 overflow-y-auto
          bg-gray-800 rounded-xl shadow-2xl border border-gray-700
          z-[100]
        ">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-3 rounded-t-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <span className="font-semibold">Emergency Bookings</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={async () => {
                  console.log('Manual refresh triggered');
                  await emergencyBookingService.forceRefresh();
                  const count = emergencyBookingService.getUnreadCount();
                  const bookings = emergencyBookingService.getPendingBookings();
                  setUnreadCount(count);
                  setPendingBookings(bookings);
                  alert(`Refreshed! Found ${bookings.length} pending bookings`);
                }}
                className="hover:bg-white/20 rounded p-1"
                title="Refresh from server"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <button onClick={() => setShowDropdown(false)} className="hover:bg-white/20 rounded p-1">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Booking List */}
          <div className="divide-y divide-gray-700">
            {pendingBookings.map((booking) => {
              const isExpanded = expandedBookingId === booking.id;
              return (
                <div key={booking.id} className="p-4 hover:bg-gray-750">
                  {/* Client Info */}
                  <div className="flex items-center gap-2 mb-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-white">
                      {booking.clientName || getClientName(booking.clientId)}
                    </span>
                  </div>

                  {/* Service & Time */}
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{formatDate(booking.requestedDate)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{formatTime(booking.requestedDate)}</span>
                    </div>
                  </div>

                  {/* Service Type */}
                  <div className="text-sm text-gray-400 mb-3">
                    {booking.serviceType}
                  </div>

                  {/* Urgency & Note */}
                  {booking.notes && (
                    <div className="text-xs text-orange-400 bg-orange-900/30 px-2 py-1 rounded mb-3">
                      "{booking.notes}"
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="bg-gray-700 rounded-lg p-3 mb-3 space-y-2">
                      <div className="text-sm">
                        <span className="text-gray-400">Phone: </span>
                        <span className="text-white">{booking.clientPhone || 'N/A'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-400">Email: </span>
                        <span className="text-white">{booking.clientEmail || 'N/A'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-400">Urgency: </span>
                        <span className={`font-medium ${booking.urgencyLevel === 'critical' ? 'text-red-400' : 'text-orange-400'}`}>
                          {booking.urgencyLevel?.toUpperCase() || 'HIGH'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-400">Time Slot: </span>
                        <span className="text-white">{booking.requestedTimeSlot || 'ASAP'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-400">Created: </span>
                        <span className="text-white">
                          {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - Row 1 */}
                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() => handleApprove(booking)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors min-w-0"
                    >
                      <Check className="h-4 w-4 flex-shrink-0" />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        // Trigger the pulsing popup for reject/counter-offer
                        window.dispatchEvent(new CustomEvent('showEmergencyRejectModal', { detail: booking }));
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors min-w-0"
                    >
                      <Clock className="h-4 w-4 flex-shrink-0" />
                      <span>Counter</span>
                    </button>
                  </div>
                  {/* Action Buttons - Row 2 */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setExpandedBookingId(isExpanded ? null : booking.id)}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors min-w-0 ${
                        isExpanded
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-600 hover:bg-gray-500 text-white'
                      }`}
                    >
                      <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <span>{isExpanded ? 'Less' : 'Details'}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer with Clear All */}
          <div className="sticky bottom-0 bg-gray-750 px-4 py-2 border-t border-gray-700">
            {pendingBookings.length > 3 && (
              <p className="text-sm text-gray-400 text-center mb-2">
                Scroll to see all {pendingBookings.length} bookings
              </p>
            )}
            <button
              onClick={async () => {
                if (window.confirm('Delete ALL emergency bookings from Firebase? This cannot be undone.')) {
                  try {
                    await emergencyBookingService.clearAll();
                    setUnreadCount(0);
                    setPendingBookings([]);
                    setShowDropdown(false);
                    alert('✅ All emergency bookings deleted from Firebase');
                  } catch (error) {
                    alert('❌ Failed to delete: ' + error);
                  }
                }
              }}
              className="w-full py-2 px-3 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg"
            >
              🗑️ Clear All from Firebase
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyBookingNotificationBadge;
